/**
 * Purchase Summary Report Page
 *
 * Main page for the purchase summary report with date range, filters and data table.
 * @see Sprint 32 - Rapport Sommaire des Achats
 * @see US-32.3 - Frontend Page et Composants
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FileText, AlertTriangle } from 'lucide-react';
import { Skeleton, EmptyState } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { usePurchaseSummaryReport } from '../hooks/usePurchaseSummaryReport';
import { PurchaseSummaryFilters } from '../components/PurchaseSummaryFilters';
import { PurchaseSummaryTable } from '../components/PurchaseSummaryTable';
import type { PurchaseSummaryReportQuery, PurchaseSummaryRow } from '@bstock/shared';

/**
 * Convert DateRange to date strings for API.
 */
function dateRangeToParams(range: DateRange): { dateFrom: string; dateTo: string } | null {
  if (!range.start || !range.end) return null;

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    dateFrom: formatDate(range.start),
    dateTo: formatDate(range.end),
  };
}

/**
 * Parse URL params for deep linking
 */
function parseUrlParams(searchParams: URLSearchParams): {
  dateRange: DateRange;
  supplierId: string;
  status: string;
} {
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');

  // Default to current month if no dates
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const defaultEnd = now;

  return {
    dateRange: {
      start: dateFrom ? new Date(dateFrom) : defaultStart,
      end: dateTo ? new Date(dateTo) : defaultEnd,
    },
    supplierId: searchParams.get('supplierId') ?? '',
    status: searchParams.get('status') ?? 'VALIDATED',
  };
}

export default function PurchaseSummaryPage() {
  const currency = useCurrency();
  const { formatDateTime } = useFormatDate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [supplierId, setSupplierId] = useState(urlParams.supplierId);
  const [status, setStatus] = useState(urlParams.status);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Build query params
  const queryParams = useMemo<PurchaseSummaryReportQuery | null>(() => {
    const dates = dateRangeToParams(dateRange);
    if (!dates) return null;

    return {
      ...dates,
      supplierId: supplierId || undefined,
      status: (status as PurchaseSummaryReportQuery['status']) || 'VALIDATED',
    };
  }, [dateRange, supplierId, status]);

  // Fetch data
  const { data: reportData, isLoading, error } = usePurchaseSummaryReport(queryParams);

  // Handlers
  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      const dates = dateRangeToParams(range);
      if (dates) {
        updateUrl({
          dateFrom: dates.dateFrom,
          dateTo: dates.dateTo,
        });
      }
    },
    [updateUrl]
  );

  const handleSupplierChange = useCallback(
    (value: string) => {
      setSupplierId(value);
      updateUrl({ supplierId: value || null });
    },
    [updateUrl]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setStatus(value);
      updateUrl({ status: value || null });
    },
    [updateUrl]
  );

  const handleRowClick = useCallback(
    (row: PurchaseSummaryRow) => {
      if (row.receiptNumber.startsWith('RET-')) {
        navigate(`/achats/retours-fournisseur/${row.goodsReceiptId}`);
      } else {
        navigate(`/achats/receptions/${row.goodsReceiptId}`);
      }
    },
    [navigate]
  );

  // Determine if we have valid dates
  const hasValidDates = dateRange.start && dateRange.end;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">Sommaire des Achats</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Recapitulatif des receptions de marchandise par fournisseur
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <PurchaseSummaryFilters
          dateRange={dateRange}
          supplierId={supplierId}
          status={status}
          onDateRangeChange={handleDateRangeChange}
          onSupplierChange={handleSupplierChange}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Content */}
      <div className="p-6 bg-neutral-50">
        {!hasValidDates ? (
          <EmptyState
            icon={FileText}
            title="Selectionnez une periode"
            description="Choisissez une date de debut et de fin pour generer le rapport"
          />
        ) : isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-96" />
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertTriangle}
            title="Erreur de chargement"
            description="Une erreur est survenue lors du chargement du rapport"
          />
        ) : reportData ? (
          <div className="space-y-6">
            {/* Summary KPI bar */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-sm text-neutral-500">Receptions</p>
                    <p className="text-xl font-semibold text-dark-500">{reportData.meta.receiptCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Fournisseurs</p>
                    <p className="text-xl font-semibold text-dark-500">{reportData.meta.supplierCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Total HT</p>
                    <p className="text-xl font-semibold text-dark-500">
                      {formatCentimes(reportData.totalExclTaxCentimes, currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">TVA</p>
                    <p className="text-xl font-semibold text-dark-500">
                      {formatCentimes(reportData.vatAmountCentimes, currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Total TTC</p>
                    <p className="text-xl font-semibold text-dark-500">
                      {formatCentimes(reportData.totalInclTaxCentimes, currency)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Table */}
            <PurchaseSummaryTable
              rows={reportData.rows}
              isLoading={false}
              onRowClick={handleRowClick}
              dateFrom={queryParams?.dateFrom}
              dateTo={queryParams?.dateTo}
            />

            {/* Meta info */}
            <div className="text-xs text-neutral-400 text-right pb-4">
              {reportData.meta.receiptCount} receptions |
              {reportData.meta.supplierCount} fournisseurs |
              Genere le {formatDateTime(reportData.meta.generatedAt)}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={FileText}
            title="Aucune donnee"
            description="Aucune reception trouvee pour cette periode et ces filtres"
          />
        )}
      </div>
    </div>
  );
}
